import { isEmpty } from 'lodash';
import { createContext, useContext, useMemo, useState } from 'react';

import { TraverseItem } from 'hb-builder/components/Renderer/TraverseItem';
import { DEFAULT_NUMBER_OF_SECTION_TO_DISPLAY } from 'hb-builder/constants/defaultNumberOfSectionToDisplay';
import { extractFirstsBodyChildren } from 'hb-builder/utils/extractFirstsBodyChildren';
import { getBodyItemInDocument } from 'hb-builder/utils/getBodyItemInDocument';
import { type DocumentItem } from 'hb-react/backoffice/types/builder/builderDocument';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

export const LazyLoadSectionContext = createContext<{
  lastSectionId?: string;
  onDisplayNextSection: (
    entries: Array<IntersectionObserverEntry>,
    isSectionObserved: boolean,
  ) => void;
}>({
  lastSectionId: undefined,
  onDisplayNextSection: () => {},
});

export const LazyLoadSectionProvider = ({
  document,
}: {
  document: DocumentItem;
}) => {
  const { flagBuilderLazyLoadSections } = useFeatureFlag();
  const [numberOfSectionToDisplay, setNumberOfSectionToDisplay] = useState(
    DEFAULT_NUMBER_OF_SECTION_TO_DISPLAY,
  );

  const body = getBodyItemInDocument(document);
  const firstsBodyChildren = flagBuilderLazyLoadSections
    ? extractFirstsBodyChildren(numberOfSectionToDisplay, body?.children)
    : body?.children;

  const newDocument: DocumentItem = body
    ? {
        ...document,
        children: [
          {
            ...body,
            children: firstsBodyChildren || [],
          },
        ],
      }
    : document;

  const lastSectionId = firstsBodyChildren?.[firstsBodyChildren.length - 1]?.id;

  const providerValue = useMemo(() => {
    const onDisplayNextSection = (
      entries: Array<IntersectionObserverEntry>,
      isSectionObserved: boolean,
    ) => {
      if (isSectionObserved) {
        return;
      }
      const isIntersecting = entries.some(
        ({ isIntersecting }) => isIntersecting,
      );

      if (isIntersecting && !isSectionObserved) {
        setNumberOfSectionToDisplay((prev) =>
          Math.min(
            Number(body?.children?.length),
            prev + DEFAULT_NUMBER_OF_SECTION_TO_DISPLAY,
          ),
        );
      }
    };

    return {
      lastSectionId,
      onDisplayNextSection,
    };
  }, [body, lastSectionId]);

  if (!(newDocument && !isEmpty(newDocument))) {
    return null;
  }

  return (
    <LazyLoadSectionContext.Provider value={providerValue}>
      <TraverseItem item={newDocument} />
    </LazyLoadSectionContext.Provider>
  );
};

export const useLazyLoadSection = () => {
  const context = useContext(LazyLoadSectionContext);

  if (context === undefined) {
    throw new Error(
      'useLazyLoadSection must be used within a LazyLoadSectionProvider',
    );
  }

  return context;
};
